import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input } from '@bruno/ui';
import {
  deleteTelegramBot,
  getTelegramSummary,
  registerTelegramBot,
  requestTelegramLinkCode,
  unlinkTelegram,
} from '../api/team';
import { useAuth } from '../auth/AuthContext';

/**
 * Per-org Telegram bot configuration plus per-agent linking. Owners
 * register a BotFather token (server validates via getMe and sets the
 * webhook). Every team member can request a deep-link `/start` code to
 * connect their Telegram user, and unlink themselves at any time.
 */
export function SettingsTelegramPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const summaryQ = useQuery({
    queryKey: ['team', 'telegram'],
    queryFn: getTelegramSummary,
  });

  const isOwner = user?.role === 'owner';

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Telegram</h1>
        <p className="text-sm text-slate-500">
          Receive pending handoffs in Telegram and reply to visitors directly from the bot.
        </p>
      </div>

      {summaryQ.isLoading ? (
        <Card>
          <div className="text-sm text-slate-500">Loading…</div>
        </Card>
      ) : summaryQ.isError ? (
        <Card>
          <div className="text-sm text-red-600">
            Failed to load Telegram settings: {(summaryQ.error as Error).message}
          </div>
        </Card>
      ) : (
        <>
          {isOwner ? (
            <BotConfigCard
              bot={summaryQ.data?.bot ?? null}
              onChanged={() => {
                void qc.invalidateQueries({ queryKey: ['team', 'telegram'] });
              }}
            />
          ) : null}
          <AgentLinkCard
            botConfigured={!!summaryQ.data?.bot}
            myLink={summaryQ.data?.myLink ?? null}
            onChanged={() => {
              void qc.invalidateQueries({ queryKey: ['team', 'telegram'] });
            }}
          />
          {!isOwner && !summaryQ.data?.bot ? (
            <Card>
              <div className="text-sm text-slate-700">
                Your account owner has not configured a Telegram bot yet. Once they do, you can link
                your Telegram account here.
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function BotConfigCard({
  bot,
  onChanged,
}: {
  bot: import('@bruno/shared').OwnerTelegramBotDTO | null;
  onChanged: () => void;
}) {
  const [token, setToken] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const register = useMutation({
    mutationFn: (t: string) => registerTelegramBot({ token: t }),
    onSuccess: () => {
      setToken('');
      onChanged();
    },
  });
  const unregister = useMutation({
    mutationFn: () => deleteTelegramBot(),
    onSuccess: () => {
      setConfirmDelete(false);
      onChanged();
    },
  });

  if (bot) {
    return (
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">@{bot.botUsername}</div>
              <div className="text-xs text-slate-500">Bot ID {bot.botId}</div>
            </div>
            <Badge tone="green">Configured</Badge>
          </div>
          <div className="text-xs text-slate-500 break-all">
            Webhook: <code className="bg-slate-100 px-1 py-0.5 rounded">{bot.webhookUrl}</code>
          </div>
          <div className="text-xs text-slate-500">
            Configured {new Date(bot.createdAt).toLocaleDateString()}.
          </div>
          {confirmDelete ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex flex-col gap-2">
              <div>
                Removing the bot revokes every team member&rsquo;s Telegram link. They will need to
                re-link if you reconfigure later.
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => unregister.mutate()}
                  loading={unregister.isPending}
                >
                  Yes, remove
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={unregister.isPending}
                >
                  Cancel
                </Button>
              </div>
              {unregister.isError ? (
                <div className="text-xs text-red-700">
                  {(unregister.error as Error).message}
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
              >
                Remove bot
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = token.trim();
    if (!trimmed) return;
    register.mutate(trimmed);
  }

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div>
          <div className="font-semibold text-slate-900">Bring your own bot</div>
          <p className="text-sm text-slate-500">
            Create a bot with{' '}
            <a
              className="text-indigo-600 hover:underline"
              href="https://t.me/BotFather"
              target="_blank"
              rel="noreferrer noopener"
            >
              @BotFather
            </a>{' '}
            and paste the HTTP API token below. We&rsquo;ll validate it, register the webhook, and
            store the token encrypted.
          </p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Input
            label="Bot token"
            type="password"
            placeholder="123456789:AAH…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
            required
            minLength={40}
            maxLength={128}
          />
          {register.isError ? (
            <div className="text-sm text-red-600">
              {parseRegisterError(register.error as Error)}
            </div>
          ) : null}
          <div>
            <Button type="submit" loading={register.isPending}>
              Register bot
            </Button>
          </div>
        </form>
        <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
          Tip: the bot token is shown to you only once by BotFather. We never display it back.
        </div>
      </div>
    </Card>
  );
}

function parseRegisterError(err: Error): string {
  // Surface the API-side `invalid_token` and `telegram_unconfigured`
  // codes as friendly messages; everything else falls through.
  const msg = err.message ?? '';
  if (msg.includes('invalid_token') || msg.toLowerCase().includes('unauthorized')) {
    return 'Telegram rejected this token. Double-check that you copied it from BotFather.';
  }
  if (msg.includes('telegram_unconfigured')) {
    return 'Telegram is not configured on the server. Ask your administrator to set SECRETS_ENC_KEY.';
  }
  return msg || 'Could not register the bot. Try again in a moment.';
}

function AgentLinkCard({
  botConfigured,
  myLink,
  onChanged,
}: {
  botConfigured: boolean;
  myLink: import('@bruno/shared').AgentTelegramLinkDTO | null;
  onChanged: () => void;
}) {
  const [issued, setIssued] = useState<{
    url: string;
    code: string;
    expiresAt: string;
  } | null>(null);

  const issue = useMutation({
    mutationFn: () => requestTelegramLinkCode(),
    onSuccess: (resp) => setIssued(resp),
  });
  const unlink = useMutation({
    mutationFn: () => unlinkTelegram(),
    onSuccess: () => {
      setIssued(null);
      onChanged();
    },
  });

  if (myLink) {
    return (
      <Card>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Your Telegram</div>
              <div className="text-xs text-slate-500">
                Linked {new Date(myLink.linkedAt).toLocaleDateString()}
                {myLink.tgUsername ? ` · @${myLink.tgUsername}` : ` · Telegram ID ${myLink.tgUserId}`}
              </div>
            </div>
            <Badge tone="green">Linked</Badge>
          </div>
          <div className="text-sm text-slate-600">
            You will receive new pending handoffs in Telegram. Reply to the visitor message in
            Telegram to send a message back to them. Use <code>/online</code>, <code>/away</code>,
            and <code>/inbox</code> in the bot for quick controls.
          </div>
          <div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (confirm('Unlink Telegram from this account?')) unlink.mutate();
              }}
              loading={unlink.isPending}
            >
              Unlink Telegram
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!botConfigured) return null;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div>
          <div className="font-semibold text-slate-900">Link your Telegram</div>
          <p className="text-sm text-slate-500">
            Open the link below from your phone or desktop Telegram app and tap <strong>Start</strong>.
            The code is single-use and expires shortly.
          </p>
        </div>
        {issued ? (
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 flex flex-col gap-2 text-sm">
            <a
              href={issued.url}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-indigo-700 hover:underline break-all"
            >
              {issued.url}
            </a>
            <div className="text-xs text-indigo-900">
              If the link doesn&rsquo;t open the bot, send <code>/start {issued.code}</code> in the bot
              chat manually.
            </div>
            <div className="text-xs text-indigo-700">
              Expires {new Date(issued.expiresAt).toLocaleTimeString()}.
            </div>
          </div>
        ) : null}
        <div>
          <Button
            size="sm"
            onClick={() => issue.mutate()}
            loading={issue.isPending}
          >
            {issued ? 'Generate a new link' : 'Generate link'}
          </Button>
        </div>
        {issue.isError ? (
          <div className="text-xs text-red-600">
            {(issue.error as Error).message}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
